import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserSchema, type SelfPatchUserInput, type User } from '@magimp/shared';
import { useApiClient } from '../api/context.js';

/**
 * @aiPurpose `PATCH /users/me` — authenticated user editing own profile
 *   (name, phone). Returns the refreshed `User` for the caller to swap
 *   into the auth store via `setSession`.
 * @aiInvariant the endpoint is fixed (no `id` argument); the caller's
 *   identity is read from the access token on the server. Never accept a
 *   user id from path or body — see `apps/backend/src/users/users-self.controller.ts`.
 *   On success, invalidates `['user', 'me']` plus the admin user-list and
 *   per-user-id caches keyed by the response's `updated.id`. Without those
 *   extra invalidations an admin who edits their own profile would keep
 *   seeing stale data on `/users` or in the user-detail drawer until an
 *   unrelated refetch.
 * @aiSecurity no token state mutated here; the caller still has to call
 *   `useAuthStore.setSession` to swap the user slice.
 */
export function usePatchSelf() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<User, unknown, SelfPatchUserInput>({
    mutationFn: (input) => client.patch<User>('/users/me', input, {}, UserSchema),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['user', 'me'] });
      qc.invalidateQueries({ queryKey: ['user', updated.id] });
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });
}
